import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../store/store";
import { setAuthFlow, setTokens } from "../../store/slices/authSlice";
import { Container, Row, Col, Card } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import { touchlessService } from "../../services/api/touchlessService";
import { savePrefillShortToken } from "../../services/api/axiosAuthInterceptor";
import { logAuthDiagnostic } from "../../services/diagnostics/authDiagnostic";
import type { OrgConfig, VisitorType } from "../../types/orgConfig";
import { showError } from "../../services/toastservice/toastService";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import { useNewVisitorSession } from "../../hooks/useNewVisitorSession";
import { setSelectedLanguage } from "../../store/slices/appSlice";
import { ROUTES } from "../../utils/constants";
import { useStrings } from "../../hooks/useStrings";
import { QRScannerCard } from "../../components/common/QRScannerCard/QRScannerCard";
import RegistrationLinkExpired from "./components/RegistrationLinkExpired/RegistrationLinkExpired";
import { getParameterByName, getCookie } from "../../utils/helpers";
import checkGif from "../../assets/images/check.gif";
import "./style/preregister.scss";

const Preregister: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const { config: orgConfig, updateConfig } = useAppConfig();
  const { startNewVisitorSession } = useNewVisitorSession();
  const {
    visitorFullName,
    visitorinfo,
    updatePreregisterId,
    updateIsPrefill,
    saveVideoUrl,
    updatePreFillNew,
    updatePreFillData,
    updateVisitorInfo,
    updateVisitorFullName,
    updateVisitorTypeId,
    updateVisitorTypeInfo,
  } = useVisitorData();
  const { themeColors } = useAppConfig();

  // State variables from Angular component
  const [token] = useState<string>(() => {
    return searchParams.get("token") || "";
  });
  const [preregisterId] = useState<string>(() => {
    return searchParams.get("id") || "";
  });

  const [loading, setLoadingState] = useState<boolean>(token ? true : false);

  const [sectionToShow, setSectionToShow] = useState<string>("");

  useEffect(() => {
    // Handle query parameters - equivalent to Angular constructor route.queryParams.subscribe
    if (preregisterId) {
      startNewVisitorSession();
      updatePreregisterId(preregisterId);
      setSectionToShow("qr-scanner");
    }

    if (token) {
      startNewVisitorSession();
      updateIsPrefill(true);
      getToken(token);
    }
  }, [preregisterId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // ngOnInit equivalent
    const error = sessionStorage.getItem("error401");
    if (error === "401") {
      // showError("Invalid or Expired QR Code. Please try again!");
      sessionStorage.removeItem("error401");
    } else {
      sessionStorage.removeItem("error401");
    }
  }, [orgConfig]);

  const getToken = async (value: string) => {
    setLoadingState(true);
    try {
      const res = await touchlessService.getPreRegistered(value);
      if (res) {
        dispatch(setTokens({ accessToken: "Bearer " + res.accessToken }));
        dispatch(setAuthFlow('visitor'));
        // Persist the short token so the global axios 401 interceptor can
        // re-mint a fresh JWT later if the visitor's session loses it
        // (iOS Safari tab eviction, stale SW shell, etc).
        savePrefillShortToken(value);
        // Diagnostic checkpoint: session just bootstrapped. Any subsequent
        // 401 / missing-token event on the same session can be correlated
        // back to this entry via Middleware's session.id.
        logAuthDiagnostic({
          reason: "session_bootstrapped",
          type : "info",
          extra: {
            accessTokenPresent: !!res?.accessToken,
            accessTokenLength: res?.accessToken?.length ?? 0,
          },
        });
        await getPreregistrationData();
      } else {
        logAuthDiagnostic({
          reason: "session_bootstrapped",
          extra: {
            accessTokenPresent: false,
            accessTokenLength: 0,
            bootstrapFailed: true,
            cause: "getPreRegistered_returned_falsy",
            resType: typeof res,
            resValue:
              res === null
                ? "null"
                : res === undefined
                  ? "undefined"
                  : "empty_object",
          },
        });
      }
    } catch (error) {
      console.error("Get token error:", error);
      sessionStorage.setItem("error401", "401");
      setSectionToShow("tokenExpired");
    } finally {
      setLoadingState(false);
    }
  };

  const getPreregistrationData = async () => {
    try {
      const baseRes = await touchlessService.getVisitorOrgConfig();
      const primaryLang = baseRes?.language ?? 'en';
      const res = primaryLang !== 'en'
        ? await touchlessService.getVisitorOrgConfig(primaryLang)
        : baseRes;
      if (res) {
        dispatch(setSelectedLanguage(primaryLang));
        sessionStorage.setItem("orgconfig", JSON.stringify(res));
        updateConfig(res);
        const middlewareService =
          await import("../../services/middleware/middlewareService")
            .then((m) => m.middlewareService)
            .catch(() => null);
        if (middlewareService) {
          middlewareService.setUser({
            id: res.deviceId,
            orgId: res.orgId,
            orgName: res.orgName,
            siteId: res.siteId,
            siteName: res.siteName,
            preregistrationId: preregisterId,
          });
        }
        await registerDataGet(res);
      }
    } catch (error) {
      // Interceptor already showed a toast (session expired / network error).
      console.error("Get preregistration data error:", error);
      setSectionToShow("somethingWentWrong");
    }
  };

  const registerDataGet = async (orgConfig_: OrgConfig) => {
    setLoadingState(true);

    try {
      const res = await touchlessService.getPreregistrationData();
      if (res) {
        const visitorType = res.visitorType;
        updateVisitorTypeId(res.visitorTypeId as string);
        updateVisitorFullName(res.fullName as string);

        if (visitorType) {
          const middlewareService = await import(
            "../../services/middleware/middlewareService"
          )
            .then((m) => m.middlewareService)
            .catch(() => null);
          if (middlewareService) {
            middlewareService.setUser({ visitorType: visitorType as string });
          }
        }

        // Find video URL for visitor type
        if (orgConfig_?.visitorType) {
          orgConfig_.visitorType.forEach((x: VisitorType) => {
            if (x.description === visitorType) {
              updateVisitorTypeInfo(x);
              if (!touchlessService.isNullUndefinedOrBlank(x.videoUrl)) {
                saveVideoUrl(x.videoUrl as string);
              }
            }
          });
        }

        if (!touchlessService.isNullUndefinedOrBlank(res.visitInfoModel)) {
          updatePreFillNew(true);
          updatePreFillData(res?.visitInfoModel as Record<string, unknown>);
          updateVisitorInfo(res as unknown as Record<string, unknown>);
          setSectionToShow("prefilled-info");
          // navigate("/confirmation");
        } else {
          updatePreFillNew(false);
          updatePreFillData(res as Record<string, unknown>);
          updateVisitorInfo(res as unknown as Record<string, unknown>);
          navigate(`/${ROUTES.PRE_FILL_DETAILS}`);
        }
      }
    } catch (error) {
      console.error("Register data get error2:", error);
      sessionStorage.setItem("error401", "401");
      setSectionToShow("somethingWentWrong");
      // navigate("/home");
    } finally {
      setLoadingState(false);
    }
  };

  const onCodeResult = async (resultString: string) => {
    if (!resultString) {
      showError("Invalid or Expired QR Code. Please try again!");
      return;
    }

    const tkn1 = getParameterByName("token", resultString);
    const tkn2 = getParameterByName("tkn", resultString);
    const tokenValue = tkn2 || tkn1 || "";

    if (!tokenValue) {
      showError("Invalid or Expired QR Code. Please try again!");
      return;
    }

    setLoadingState(true);
    try {
      const res = await touchlessService.getAuthenticate(!!tkn2, tokenValue);
      dispatch(setTokens({ accessToken: "Bearer " + res.accessToken }));

      const baseResponse = await touchlessService.getOrgConfig();
      const primaryLang2 = baseResponse?.language ?? 'en';
      const response = primaryLang2 !== 'en'
        ? await touchlessService.getOrgConfig(primaryLang2)
        : baseResponse;
      if (response) {
        dispatch(setSelectedLanguage(primaryLang2));
        updateConfig(response);
        sessionStorage.setItem("orgconfig", JSON.stringify(response));
        const middlewareService =
          await import("../../services/middleware/middlewareService")
            .then((m) => m.middlewareService)
            .catch(() => null);
        if (middlewareService) {
          middlewareService.setUser({
            id: response.deviceId,
            orgId: response.orgId,
            orgName: response.orgName,
            siteId: response.siteId,
            siteName: response.siteName,
            preregistrationId: preregisterId,
          });
        }
        if (getCookie("visitId")) {
          navigate(`/${ROUTES.SIGN_OUT}`, {
            replace: true,
            state: { nextRoute: "/visitorform" },
          });
        } else {
          navigate("/visitorform");
        }
      } else {
        sessionStorage.setItem("error401", "401");
        navigate("/home");
      }
    } catch (error) {
      console.error("QR code authentication error:", error);
      sessionStorage.setItem("error401", "401");
      navigate("/home");
    } finally {
      setLoadingState(false);
    }
  };

  const handleCodeResult = useCallback((decodedText: string) => {
    onCodeResult(decodedText);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScannerError = useCallback((message: string) => {
    showError(message);
  }, []);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          zIndex: 9999,
        }}
        data-testid="preregister-loading-overlay"
      >
        <ClipLoader color={themeColors.btnColor} size={50} />
      </div>
    );
  }

  return (
    <>
      <div
        className="screen-contactless"
        id="preregister-page show-back"
        data-testid="preregister-screen-contactless"
      >
        {sectionToShow === "qr-scanner" && (
          <Container data-testid="preregister-qr-container">
            <Row
              className="justify-content-center"
              data-testid="preregister-qr-row"
            >
              <Col
                xl={5}
                lg={6}
                md={8}
                sm={10}
                data-testid="preregister-qr-col"
              >
                <Card
                  className="card-contactless"
                  data-testid="preregister-qr-card"
                >
                  <Card.Body data-testid="preregister-qr-card-body">
                    <h2 data-testid="preregister-qr-title">
                      {S.PREREGISTER.QR_TITLE}
                    </h2>
                    <h6 data-testid="preregister-qr-subtitle">
                      {S.PREREGISTER.QR_SUBTITLE}
                    </h6>
                    <QRScannerCard
                      onCodeResult={handleCodeResult}
                      onError={handleScannerError}
                      isLoading={loading}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        )}

        {sectionToShow === "tokenExpired" && <RegistrationLinkExpired />}
        {sectionToShow === "somethingWentWrong" && (
          <Container data-testid="preregister-container">
            <div className="screen-header-logo">
              <i className="bi bi-clock" style={{ fontSize: "2rem" }}></i>
            </div>
            <Row
              className="justify-content-center"
              data-testid="preregister-row"
            >
              <Col xl={6} lg={8} md={8} sm={12} data-testid="preregister-col">
                <Card
                  className="card-contactless"
                  data-testid="preregister-card"
                >
                  <Card.Body data-testid="preregister-card-body">
                    <h2 data-testid="preregister-title">
                      {S.PREREGISTER.ERROR_TITLE}
                    </h2>
                    <p data-testid="preregister-subtitle">
                      {S.PREREGISTER.ERROR_SUBTITLE}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        )}

        {sectionToShow === "prefilled-info" && (
          <div
            className="prefilled-info-screen"
            data-testid="preregister-prefilled"
          >
            {/* Check icon */}
            <div className="prefilled-check-icon">
              <img src={checkGif} alt="checkmark" style={{ height: 165 }} />
            </div>

            {/* Heading */}
            <div className="prefilled-heading">
              <h2>{S.PREREGISTER.ALL_SET_TITLE}</h2>
              <p>{S.PREREGISTER.ALL_SUB_TITLE}</p>
            </div>

            {/* Details card */}
            <div className="prefill-card prefilled-details-card">
              <div className="prefilled-detail-row">
                <span className="prefilled-detail-label">{S.PREREGISTER.DETAIL_LABEL_VISITOR}</span>
                <span className="prefilled-detail-value">
                  {visitorFullName || "—"}
                </span>
              </div>
              <div className="prefilled-detail-row">
                <span className="prefilled-detail-label">{S.PREREGISTER.DETAIL_LABEL_EMAIL}</span>
                <span className="prefilled-detail-value">
                  {(visitorinfo?.email as string) || "—"}
                </span>
              </div>
              <div className="prefilled-detail-row">
                <span className="prefilled-detail-label">{S.PREREGISTER.DETAIL_LABEL_HOST}</span>
                <span className="prefilled-detail-value">
                  {(visitorinfo?.hostName as string) || S.PREREGISTER.DETAIL_HOST_PLACEHOLDER}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="prefilled-actions" hidden={true}>
              <button
                type="button"
                className="btn btn-primary btn-shadow flex-1"
                onClick={() => navigate(`/${ROUTES.PRE_FILL_DETAILS}`)}
              >
                {S.PREREGISTER.BTN_CHECK_DETAILS}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary flex-1 border"
                onClick={() => navigate(`/${ROUTES.CONFIRMATION}`)}
              >
                {S.PREREGISTER.BTN_VIEW_CHECKIN}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Preregister;
